/**
 * Single source of truth for reading price entries from untrusted/legacy JSON
 * into the current `IPriceDataInfo` shape. Used by the backend import reducers,
 * the admin JSON import and the batch-case debug tooling — they must all accept
 * the same inputs and apply the same defaults.
 */
import { type IPriceDataInfo, PriceUnit } from "./types";

const PRICE_UNITS = new Set<string>(Object.values(PriceUnit));

function normalizeUnit(unit: unknown): PriceUnit {
  return typeof unit === "string" && PRICE_UNITS.has(unit)
    ? (unit as PriceUnit)
    : PriceUnit.Unknown;
}

function toNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function toText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Ensures all required fields are present on a price data entry, filling in
 * sensible defaults for any missing values. Returns null for entries without
 * an article number (`name`).
 */
export function normalizePriceEntry(
  entry: Partial<IPriceDataInfo>,
): IPriceDataInfo | null {
  const name = typeof entry.name === "string" ? entry.name.trim() : "";
  if (!name) {
    return null;
  }
  return {
    name,
    description: entry.description ?? "",
    partCategory: entry.partCategory ?? "",
    pricePurchase: entry.pricePurchase ?? 0,
    unit: normalizeUnit(entry.unit),
    weightGramms: entry.weightGramms ?? 0,
    workingTimeMinutes: entry.workingTimeMinutes ?? 0,
    countRivets: entry.countRivets ?? 0,
    customerName: entry.customerName ?? "",
  };
}

/** First non-empty string among the candidates, trimmed. */
function firstNonEmpty(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const text = toText(candidate).trim();
    if (text) return text;
  }
  return "";
}

/**
 * Maps one raw JSON entry to a fully normalized price entry. Accepts the
 * current camelCase shape, rows of the generic atom export (`{context, atom}`)
 * and legacy (pre-entity, PascalCase) entries as found in legacy price dumps and
 * old exports.
 *
 * Legacy entries carry the article number in
 * `MerchandiseManagementArticleNumber` and the label in `Name` — note that
 * legacy `Name` maps to `description`, not to `name`. `fallbackName` is used
 * when the record has no article number of its own; pass the key when reading
 * a `Record<articleNumber, entry>` dump.
 *
 * Returns null for entries without an article number.
 */
export function toPriceDataInfo(
  raw: unknown,
  fallbackName?: string,
): IPriceDataInfo | null {
  if (!raw || typeof raw !== "object") return null;
  let entry = raw as Record<string, unknown>;
  if (entry.atom && typeof entry.atom === "object") {
    entry = entry.atom as Record<string, unknown>;
  }

  const name = firstNonEmpty(
    entry.name,
    entry.MerchandiseManagementArticleNumber,
    fallbackName,
  );
  if (!name) return null;

  return {
    name,
    description: toText(entry.description ?? entry.Name),
    partCategory: toText(entry.partCategory ?? entry.PartCategory),
    pricePurchase: toNumber(entry.pricePurchase ?? entry.PricePurchase),
    unit: normalizeUnit(entry.unit ?? entry.Unit),
    weightGramms: toNumber(entry.weightGramms ?? entry.WeightGramms),
    workingTimeMinutes: toNumber(
      entry.workingTimeMinutes ?? entry.WorkingTimeMinutes,
    ),
    countRivets: toNumber(entry.countRivets ?? entry.CountRivets),
    customerName: toText(entry.customerName ?? entry.CustomerName),
  };
}
